import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';
import _ from 'lodash';
import {
  cognitoSignup,
  checkEmailAlreadyExists,
  getRegions,
  getTermAndCondition,
} from './../../services/login/loginApi-service';
import { Common } from '../../utils/constants';
import { companyNameReplace } from '../../utils';
import { store } from '../../store';
import { ISignUpProps } from '../../types/propTypes';
import type { ISignUpFormField, ISignUpFormValues, ITermsConditionData } from '../../models/login';
import { IModelState } from '../../types/stateTypes';
import { getAPIKey } from '../../services/login/login-service';
import { IRegionData } from '../../models/profile';
import { env } from 'config/env';

export const SignUpFormHook = ({ setEmail, setPassword, onChangeStage, onInvalidCaptcha }: ISignUpProps) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const parameters = new URLSearchParams(window.location.search);
  const { t, i18n } = useTranslation();

  const stage =
    parameters.get('stage') && parameters.get('stage') !== 'undefined'
      ? Number(parameters.get('stage'))
      : Common.AuthStage.SIGNIN;

  const [formValues, setFormValues] = useState<ISignUpFormField>({
    email: parameters?.get('email') && parameters?.get('email') !== 'undefined' ? `${parameters?.get('email')}` : '',
    password: '',
    recaptchaCode: '',
    term_and_condition: {
      id: 0,
      accepted: false,
    },
  });

  const [modal, setModal] = useState<IModelState>({
    showModel: false,
    header: '',
    body: '',
    callBack: null,
  });

  const [siteKey, setSiteKey] = useState<string>('');

  const [selectedRegion, setSelectedRegion] = useState<IRegionData | null>(null);
  const [regions, setRegions] = useState<Array<IRegionData>>([]);

  const [termsAndConditionData, setTermsAndConditionData] = useState<ITermsConditionData>();
  const [termsModal, setTermsModal] = useState({
    showModal: false,
    body: '',
    callBack: null,
  });

  const [termsWarningModal, setTermsWarningModal] = useState({
    showModal: false,
  });

  const colorStyles = {
    control: (base) => ({
      ...base,
      border: 0,
      borderColor: '#CBCED7',
      boxShadow: 'none',
    }),
    option: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: isFocused ? 'var(--themeColor)' : null,
      color: isFocused ? 'white' : '#333333',
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--themeColor)',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '0',
      color: '#929292',
      position: 'relative',
      right: '10px',
    }),
    valueContainer: (base) => ({
      ...base,
    }),
    menu: (base) => ({
      ...base,
      minWidth: '110px',
      zIndex: '999',
      margin: 0,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10000,
    }),
    menuList: (base) => ({
      ...base,
      overflowY: 'auto',
      scrollbarWidth: 'thin',
      scrollbarColor: 'grey transparent',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'grey',
        borderRadius: '3px',
      },
      zIndex: '10000',
    }),
  };

  useEffect(() => {
    getRegion(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    getRecaptchaSiteKey();
    getTermsAndConditions();
  }, []);

  const getRegion = async (language?: string) => {
    try {
      await getRegions(language).then((res) => {
        if (res && res.data && res.data.rows) {
          setRegions(res.data.rows);
          setSelectedRegion(res.data.rows && res.data.rows.filter((item) => item.code === env.VITE_PORTAL_REGION)[0]);
        } else {
          setRegions([]);
          setSelectedRegion(null);
        }
      });
    } catch (_e) {
      setRegions([]);
      setSelectedRegion(null);
    }
  };

  const getTermsAndConditions = async () => {
    try {
      const response = await getTermAndCondition();
      setTermsAndConditionData(response.Data);
      setFormValues((prev) => ({
        ...prev,
        term_and_condition: {
          id: response.Data.id,
          accepted: false,
        },
      }));
    } catch (error) {
      if (error.response?.message) {
        toast.error(error.response.message);
      } else {
        toast.error(error.toString());
      }
    }
  };

  const handleRegionChange = (selectedRegion: IRegionData, email) => {
    setSelectedRegion(selectedRegion);
    if (stage === Common.AuthStage.SIGNUP) {
      window.location.href = `${selectedRegion.driver_portal_host_url}/login?stage=3&email=${email}`;
    } else if (stage === Common.AuthStage.SIGNIN) {
      window.location.href = `${selectedRegion.driver_portal_host_url}/login?stage=0&email=${email}`;
    }
  };

  const handleCheckChange = () => {
    setFormValues((prevVal) => ({
      ...prevVal,
      term_and_condition: {
        ...prevVal.term_and_condition,
        accepted: !prevVal.term_and_condition.accepted,
      },
    }));
  };

  const handleTermsModal = (body: string, show: boolean, callBack) => {
    setTermsModal({
      body: body,
      showModal: show,
      callBack: callBack,
    });
  };

  const closeTermsModal = () => {
    setTermsModal({
      body: '',
      showModal: false,
      callBack: null,
    });
  };

  const acceptTermsFromModal = (setFieldValue) => {
    if (setFieldValue) {
      setFieldValue('term_and_condition.accepted', true);
    }
    closeTermsModal();
  };

  const openTermsWarningModal = () => {
    setTermsWarningModal({
      showModal: true,
    });
  };

  const closeTermsWarningModal = (setFieldValue) => {
    if (setFieldValue) {
      setFieldValue('term_and_condition.accepted', true);
    }
    setTermsWarningModal({
      showModal: false,
    });
  };

  const handleModal = (body: string, header: string, callBack: () => void) => {
    setModal({
      body: body,
      header: header,
      callBack: callBack,
      showModel: !modal.showModel,
    });
  };

  const closeModal = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setModal({
      body: '',
      header: '',
      callBack: null,
      showModel: false,
    });
  };

  const handleSubmitAfterEmailCheck = async (value: ISignUpFormField, isRecaptchaV3: boolean) => {
    const userLang = store.getState()?.UserData?.language
      ? store.getState()?.UserData?.language
      : store.getState()?.LanguageInfo?.defaultLanguage;
    return cognitoSignup({
      ...value,
      captcha_version: isRecaptchaV3 ? Common.CaptchaVersion.RECAPTCHA_V3 : Common.CaptchaVersion.RECAPTCHA_V2,
      language_preference_id: userLang?.id,
      language_preference_name: userLang?.code,
    })
      .then(async (_res) => {
        sessionStorage.setItem(
          Common.SIGNUP_CREDENTIALS,
          JSON.stringify({
            email: value.email,
            password: value.password,
          }),
        );
        setEmail(value.email);
        setPassword(value.password);
        onChangeStage(Common.AuthStage.SIGNUP_OTP);
      })
      .catch((err) => {
        if (err?.name === Common.Authentication.UsernameExistsException) {
          toast.error(t('signup.toast.alreadyExist'));
          closeModal();
          return;
        }
        if (err?.name === Common.Authentication.InvalidPasswordException) {
          toast.error(t('signup.toast.invalidPassword'));
          closeModal();
          return;
        }
        if (err?.name === Common.Authentication.referaalCodeMisMatch) {
          toast.error(t('signup.toast.referralCodeInvalid'));
          closeModal();
          return;
        }
        if (
          err?.name === Common.Authentication.UserLambdaValidationException &&
          err.message?.includes('CaptchaV3Failed')
        ) {
          onInvalidCaptcha && onInvalidCaptcha();
          closeModal();
          return;
        }
        toast.error(t('signup.toast.error'));
        closeModal();
      });
  };

  const handleSubmit = async (value: ISignUpFormValues, isRecaptchaV3: boolean) => {
    try {
      const input = {
        email: value.email.toLowerCase(),
        log_type: 4,
      };
      const response = await checkEmailAlreadyExists(input);
      if (response.Data.log_added) {
        toast.error('Email address which you have entered is already Exists');
        return;
      }

      if (response.Data.type_of_log.length === 0) {
        handleSubmitAfterEmailCheck(value, isRecaptchaV3);
        return;
      }

      const accountTypeMap: Record<string, string> = {
        1: 'Google',
        2: 'Apple',
        3: 'Facebook',
      };

      const existingEmails = response.Data.type_of_log.map((type: number) => ({
        account_type: accountTypeMap[type],
      }));

      let modalMessage = '';
      modalMessage = companyNameReplace(t('signup.label.modalMessage'));
      const emailString = `<span class='themeTextColor'>${value.email.toString()}</span>`;
      modalMessage = modalMessage.replace('##EMAIL##', emailString);

      const accounts = existingEmails
        .map(({ account_type }) => {
          const displayName = account_type ?? companyNameReplace(t('signup.label.instavoltUserAccount'));
          return `<span class='themeTextColor'>${displayName}</span>`;
        })
        .join(', ');

      modalMessage = modalMessage.replace('##FEDERATEDACCOUNT##', accounts);
      handleModal(modalMessage, companyNameReplace(t('signup.label.instavolt')), () =>
        handleSubmitAfterEmailCheck(value, isRecaptchaV3),
      );
    } catch (error) {
      const isInvalidCaptcha =
        error?.response?.data?.error_code === 'InvalidCaptcha' ||
        (typeof error?.response?.data?.message === 'string' &&
          error.response.data.message.toLowerCase().includes('invalid captcha'));
      if (isInvalidCaptcha) {
        onInvalidCaptcha && onInvalidCaptcha();
        return;
      }
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.toString());
      }
    }
  };

  const getRecaptchaSiteKey = async () => {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    });
    const siteKey = config.data.find(
      (item) => item.reference_code === Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY,
    )?.value;
    setSiteKey(siteKey ?? '');
  };

  return {
    formValues,
    modal,
    recaptchaRef,
    handleSubmitAfterEmailCheck,
    handleModal,
    closeModal,
    handleSubmit,
    getRecaptchaSiteKey,
    siteKey,
    colorStyles,
    selectedRegion,
    regions,
    handleRegionChange,
    handleCheckChange,
    termsAndConditionData,
    termsModal,
    handleTermsModal,
    closeTermsModal,
    acceptTermsFromModal,
    termsWarningModal,
    openTermsWarningModal,
    closeTermsWarningModal,
  };
};
